import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { supabase } from '../../lib/supabase';
import type { User } from '../../contexts/auth.types';
import { SESSION_TIMEOUT_MS } from './timeoutUtils';
import { loadUserProfile } from './loadUserProfile';
import { getSessionWithRetry } from './sessionUtils';
import { createFallbackUser, createBasicUser, isClientSessionMissingClientKeys } from './authUtils';

export function useAuthSynchronization(
  user: User | null,
  setUser: Dispatch<SetStateAction<User | null>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  clearStaleAuthStorage: () => void
) {
  const initialLoadCompleteRef = useRef(false);
  const initializationStartedRef = useRef(false);
  
  // Track last visibility change to avoid duplicate refreshes
  const lastVisibilityRefreshRef = useRef<number>(0);
  const visibilityRefreshInProgressRef = useRef<boolean>(false);
  // Ref to access current user in visibility handler without triggering re-renders
  const userRef = useRef<User | null>(user);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refreshUser = useCallback(async () => {
    const session = await supabase.auth.getSession();
    if (session.data.session?.user) {
      const userData = await loadUserProfile(session.data.session.user.id, session.data.session.user.email);
      setUser(userData);
    }
  }, [setUser]);

  useEffect(() => {
    if (initializationStartedRef.current) {
      return;
    }
    initializationStartedRef.current = true;

    let mounted = true;

    const setUserAndFinishLoading = (userData: User | null) => {
      if (!mounted) return;
      setUser(userData);
      if (!initialLoadCompleteRef.current) {
        setIsLoading(false);
        initialLoadCompleteRef.current = true;
      }
    };

    const getInitialSession = async () => {
      try {
        type GetSessionResult = Awaited<ReturnType<typeof supabase.auth.getSession>>;
        type TimeoutResult = { timedOut: true };

        const sessionPromise = getSessionWithRetry(3, 500);

        const sessionResult = await Promise.race<GetSessionResult | TimeoutResult | null>([
          sessionPromise,
          new Promise<TimeoutResult>((resolve) => {
            setTimeout(() => {
              if (import.meta.env.DEV) {
                console.warn(`⏱️ getSession took longer than ${SESSION_TIMEOUT_MS}ms; proceeding without initial session (storage preserved)`);
              }
              resolve({ timedOut: true });
            }, SESSION_TIMEOUT_MS);
          }),
        ]);

        if (!mounted) return;

        if (sessionResult === null || (sessionResult && 'timedOut' in sessionResult)) {
          console.warn('⏱️ Session check timed out or failed - proceeding without session (storage preserved)');
          setUserAndFinishLoading(null);
          return;
        }

        const { data: { session } } = sessionResult;

        if (!mounted) return;

        if (session?.user) {
          try {
            const userData = await loadUserProfile(session.user.id, session.user.email);
            if (!mounted) return;

            if (userData) {
              setUserAndFinishLoading(userData);
            } else {
              setUserAndFinishLoading(createFallbackUser(session.user));
            }
          } catch (profileError) {
            console.error('💥 Profile loading failed:', profileError);
            if (!mounted) return;

            setUserAndFinishLoading(createBasicUser(session.user));
          }
        } else {
          setUserAndFinishLoading(null);
        }
      } catch (error) {
        const isAbortError = error instanceof Error &&
          (error.name === 'AbortError' || error.message.includes('signal is aborted'));

        if (isAbortError) {
          console.warn('⚠️ Session check aborted (tab may have been inactive) - will retry on visibility change');
        } else {
          console.error('❌ Error getting initial session (preserving storage):', error);
        }
        setUserAndFinishLoading(null);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!mounted) return;
      if (!initialLoadCompleteRef.current) return;

      const now = Date.now();
      if (now - lastVisibilityRefreshRef.current < 5000) {
        return;
      }

      if (visibilityRefreshInProgressRef.current) {
        return;
      }

      lastVisibilityRefreshRef.current = now;
      visibilityRefreshInProgressRef.current = true;

      try {
        const result = await getSessionWithRetry(3, 500);
        if (!mounted) return;

        if (result?.data?.session?.user) {
          const currentUser = userRef.current;
          if (
            !currentUser ||
            currentUser.id !== result.data.session.user.id ||
            isClientSessionMissingClientKeys(currentUser)
          ) {
            const userData = await loadUserProfile(result.data.session.user.id, result.data.session.user.email);
            if (!mounted) return;
            if (userData) {
              setUser(userData);
            }
          }
        } else if (userRef.current) {
          console.warn('⚠️ Session expired while tab was inactive');
          setUser(null);
          clearStaleAuthStorage();
        }
      } catch (err) {
        const isAbortError = err instanceof Error &&
          (err.name === 'AbortError' || err.message.includes('signal is aborted'));

        if (isAbortError) {
          console.warn('⚠️ Session check still failing after retry - browser may need refresh');
        } else {
          console.error('❌ Error refreshing session on visibility change:', err);
        }
      } finally {
        visibilityRefreshInProgressRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {


      if (!initialLoadCompleteRef.current) {
        return;
      }

      if (!mounted) return;

      if (session?.user) {
        const currentUser = userRef.current;
        if (
          currentUser &&
          currentUser.id === session.user.id &&
          currentUser.permissions.length > 1 &&
          !isClientSessionMissingClientKeys(currentUser) &&
          (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')
        ) {
          return;
        }

        const sessionUser = session.user;
        setTimeout(async () => {
          if (!mounted) return;

          try {
            const userData = await loadUserProfile(sessionUser.id, sessionUser.email);
            if (!mounted) return;

            if (userData) {
              setUser(userData);
            } else {
              setUser(prev => {
                if (prev) {
                  console.warn('⚠️ [AuthContext] Profile load returned null during background refresh; keeping existing user state to prevent Access Denied');
                  return prev;
                }

                const fallbackUser = createFallbackUser(sessionUser);
                return fallbackUser;
              });
            }
          } catch (profileError) {
            console.error('💥 Profile loading failed in auth change:', profileError);
            if (!mounted) return;

            setUser(prev => {
              if (prev) {
                console.warn('⚠️ [AuthContext] Profile load threw during background refresh; keeping existing user state to prevent Access Denied');
                return prev;
              }

              const basicUser = createBasicUser(sessionUser);
              return basicUser;
            });
          }
        }, 0);
      } else {
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      initializationStartedRef.current = false;
    };
  }, [clearStaleAuthStorage, setUser, setIsLoading]);

  return { refreshUser, loadUserProfile };
}
